import { useState, type ReactNode } from "react";
import { useNavigate } from "@tanstack/react-router";

import { logout } from "@/api/auth";
import { Icon, type IconName } from "@/components/hifi/Icon";
import { IconBtn } from "@/components/hifi/IconBtn";
import { Pill } from "@/components/hifi/Pill";
import { Avatar, Kbd, Label } from "@/components/hifi/misc";
import { useAuthStore } from "@/stores/auth";

export type NavKey =
  | "dashboard"
  | "assessments"
  | "assets"
  | "threats"
  | "controls"
  | "incidents"
  | "tasks"
  | "reports"
  | "map"
  | "audit"
  | "settings"
  | "admin";

interface NavItem {
  key: NavKey;
  label: string;
  icon: IconName;
  path: string;
  badge?: string;
  badgeTone?: "bad" | "default";
}

const PRIMARY_NAV: NavItem[] = [
  { key: "dashboard", label: "Dashboard", icon: "grid", path: "/" },
  { key: "assessments", label: "Assessments", icon: "list", path: "/assessments" },
  { key: "assets", label: "Assets", icon: "cube", path: "/assets" },
  { key: "threats", label: "Threats", icon: "triangle", path: "/threats" },
  { key: "controls", label: "Countermeasures", icon: "shield", path: "/countermeasures" },
  { key: "incidents", label: "Incidents", icon: "alert", path: "/incidents", badgeTone: "bad" },
  { key: "tasks", label: "Tasks", icon: "checkBox", path: "/action-plans" },
  { key: "reports", label: "Reports", icon: "file", path: "/reports" },
];

const SECONDARY_NAV: NavItem[] = [
  { key: "map", label: "Map view", icon: "map", path: "/assessments" },
  { key: "audit", label: "Audit log", icon: "history", path: "/audit" },
  { key: "settings", label: "Settings", icon: "settings", path: "/admin" },
];

interface SidebarProps {
  active: NavKey;
}

const STORAGE_KEY = "csmp-sidebar-collapsed";

export function Sidebar({ active }: SidebarProps): ReactNode {
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) === "1";
    } catch {
      return false;
    }
  });

  const toggle = (): void => {
    setCollapsed((c) => {
      const next = !c;
      try {
        localStorage.setItem(STORAGE_KEY, next ? "1" : "0");
      } catch {
        // ignore
      }
      return next;
    });
  };

  if (collapsed) return <SidebarCollapsed active={active} onExpand={toggle} />;
  return <SidebarExpanded active={active} onCollapse={toggle} />;
}

function SidebarExpanded({
  active,
  onCollapse,
}: {
  active: NavKey;
  onCollapse: () => void;
}): ReactNode {
  const user = useAuthStore((s) => s.user);
  const clear = useAuthStore((s) => s.clear);
  const navigate = useNavigate();
  const initials =
    user?.first_name && user?.last_name
      ? `${user.first_name[0]}${user.last_name[0]}`
      : (user?.email?.slice(0, 2).toUpperCase() ?? "?");
  const displayName =
    user?.first_name && user?.last_name
      ? `${user.first_name} ${user.last_name}`
      : (user?.email ?? "");
  const role = user?.role?.toLowerCase().replace(/_/g, " ") ?? "";

  async function handleLogout(): Promise<void> {
    try {
      await logout();
    } finally {
      clear();
      await navigate({ to: "/login" });
    }
  }

  return (
    <aside className="relative flex w-[220px] flex-shrink-0 flex-col border-r border-n-150 bg-n-50">
      <button
        onClick={onCollapse}
        title="Collapse sidebar"
        className="absolute top-[18px] -right-[10px] z-[5] flex h-5 w-5 items-center justify-center rounded-full border border-n-200 bg-white p-0 shadow-1"
      >
        <Icon name="chevLeft" size={11} color="var(--n-600)" />
      </button>

      <div className="border-b border-n-150 px-3 pt-3 pb-2.5">
        <button className="flex w-full items-center gap-2 rounded-2 border border-n-200 bg-white px-2 py-1.5 shadow-1">
          <div
            className="flex h-[22px] w-[22px] items-center justify-center rounded-1 text-[11px] font-bold text-white"
            style={{
              background: "linear-gradient(135deg, var(--a-500), var(--a-700))",
            }}
          >
            {user?.tenant_id ? "C" : "C"}
          </div>
          <div className="min-w-0 flex-1 text-left">
            <div className="text-[12px] font-semibold leading-tight text-n-900 truncate">
              CSMP Workspace
            </div>
            <div className="text-[10px] leading-tight text-n-500">
              {user?.tenant_id ? `TID ${user.tenant_id.slice(0, 6)}` : "—"}
            </div>
          </div>
          <Icon name="chevDown" size={12} color="var(--n-400)" />
        </button>
      </div>

      <div className="px-3 pt-2.5 pb-1.5">
        <div className="flex items-center gap-1.5 rounded-2 border border-n-200 bg-white px-2 py-[5px]">
          <Icon name="search" size={12} color="var(--n-400)" />
          <span className="flex-1 text-[12px] text-n-400">Search…</span>
          <Kbd>⌘K</Kbd>
        </div>
      </div>

      <nav className="flex flex-col gap-px px-2 py-1.5">
        {PRIMARY_NAV.map((it) => (
          <NavLink key={it.key} item={it} active={it.key === active} />
        ))}
      </nav>

      <div className="px-3 pt-2.5 pb-1">
        <Label>Org</Label>
      </div>
      <nav className="flex flex-col gap-px px-2">
        {SECONDARY_NAV.map((it) => (
          <NavLink key={it.key} item={it} active={it.key === active} small />
        ))}
      </nav>

      <div className="flex-1" />

      <div className="border-t border-n-150 px-3 py-2.5">
        <Label className="mb-1.5">Compliance</Label>
        <div className="flex flex-wrap gap-[3px]">
          <Pill variant="accent" className="!px-1.5 !py-[1px] !text-[10px]">
            ISO 31000
          </Pill>
          <Pill variant="accent" className="!px-1.5 !py-[1px] !text-[10px]">
            NIS2
          </Pill>
          <Pill className="!px-1.5 !py-[1px] !text-[10px]">CER</Pill>
        </div>
      </div>

      <div className="flex items-center gap-2 border-t border-n-150 px-3 py-2.5">
        <Avatar initials={initials} size={28} color="#fff" bg="var(--n-800)" />
        <div className="min-w-0 flex-1">
          <div className="truncate text-[12px] font-semibold leading-tight text-n-900">
            {displayName}
          </div>
          <div className="text-[10px] capitalize leading-tight text-n-500">
            {role}
          </div>
        </div>
        <IconBtn
          name="logout"
          size="sm"
          label="Sign out"
          onClick={() => {
            void handleLogout();
          }}
        />
      </div>
    </aside>
  );
}

function NavLink({
  item,
  active,
  small,
}: {
  item: NavItem;
  active: boolean;
  small?: boolean;
}): ReactNode {
  const navigate = useNavigate();
  return (
    <a
      href={item.path}
      onClick={(e) => {
        e.preventDefault();
        void navigate({ to: item.path });
      }}
      className={`flex items-center gap-2 rounded-2 border px-2 ${small ? "py-[5px] text-[12px]" : "py-1.5 text-[13px]"} font-medium no-underline cursor-pointer ${
        active
          ? "border-n-150 bg-white font-semibold text-n-900 shadow-1"
          : "border-transparent text-n-600 hover:bg-n-100/60"
      }`}
    >
      <Icon
        name={item.icon}
        size={small ? 13 : 14}
        color={active ? "var(--a-600)" : "var(--n-500)"}
      />
      <span className="flex-1">{item.label}</span>
      {item.badge && (
        <span
          className={`inline-flex h-4 min-w-[18px] items-center justify-center rounded-full px-1.5 font-mono text-[10px] font-semibold ${
            item.badgeTone === "bad"
              ? "bg-bad-bg text-bad"
              : "bg-n-100 text-n-600"
          }`}
        >
          {item.badge}
        </span>
      )}
    </a>
  );
}

function SidebarCollapsed({
  active,
  onExpand,
}: {
  active: NavKey;
  onExpand: () => void;
}): ReactNode {
  const user = useAuthStore((s) => s.user);
  const initials =
    user?.first_name && user?.last_name
      ? `${user.first_name[0]}${user.last_name[0]}`
      : (user?.email?.slice(0, 2).toUpperCase() ?? "?");
  return (
    <aside className="relative flex w-14 flex-shrink-0 flex-col items-center gap-[2px] border-r border-n-150 bg-n-50 py-3">
      <button
        onClick={onExpand}
        title="Expand sidebar"
        className="absolute top-[18px] -right-[10px] z-[5] flex h-5 w-5 items-center justify-center rounded-full border border-n-200 bg-white p-0 shadow-1"
      >
        <Icon name="chevRight" size={11} color="var(--n-600)" />
      </button>

      <div
        className="mb-2.5 flex h-[30px] w-[30px] items-center justify-center rounded-2 text-[13px] font-bold text-white"
        style={{
          background: "linear-gradient(135deg, var(--a-500), var(--a-700))",
        }}
      >
        C
      </div>

      <button
        title="Search (⌘K)"
        className="mb-1 flex h-[30px] w-[34px] items-center justify-center rounded-2 bg-transparent"
      >
        <Icon name="search" size={14} color="var(--n-500)" />
      </button>

      <div className="my-[2px] h-px w-7 bg-n-150" />

      {PRIMARY_NAV.map((it) => (
        <CollapsedLink key={it.key} item={it} active={it.key === active} />
      ))}

      <div className="my-1.5 h-px w-7 bg-n-150" />

      {SECONDARY_NAV.map((it) => (
        <CollapsedLink key={it.key} item={it} active={it.key === active} secondary />
      ))}

      <div className="flex-1" />

      <Avatar initials={initials} size={28} color="#fff" bg="var(--n-800)" />
    </aside>
  );
}

function CollapsedLink({
  item,
  active,
  secondary,
}: {
  item: NavItem;
  active: boolean;
  secondary?: boolean;
}): ReactNode {
  const navigate = useNavigate();
  return (
    <a
      href={item.path}
      title={item.label}
      onClick={(e) => {
        e.preventDefault();
        void navigate({ to: item.path });
      }}
      className={`relative flex w-[34px] cursor-pointer items-center justify-center rounded-2 border ${
        secondary ? "h-7" : "h-[30px]"
      } ${active ? "border-n-150 bg-white shadow-1" : "border-transparent"}`}
    >
      <Icon
        name={item.icon}
        size={secondary ? 13 : 14}
        color={active ? "var(--a-600)" : "var(--n-500)"}
      />
      {item.badge && (
        <span
          className="absolute -right-0.5 -top-0.5 inline-flex h-3 min-w-[12px] items-center justify-center rounded-full border-[1.5px] border-n-50 px-1 font-mono text-[8px] font-bold text-white"
          style={{
            background: item.badgeTone === "bad" ? "var(--bad)" : "var(--n-600)",
          }}
        >
          {item.badge}
        </span>
      )}
    </a>
  );
}

